

import React, { useState, useEffect, useMemo } from "react";
import {
    Row, Col, Rate, Button, Tag, Spin, Alert, message, Modal,
    Typography, Input, Select, Slider, Space, Tooltip, Badge,
    Empty, Dropdown, Menu
} from "antd";
import {
    ShoppingCartOutlined, EyeOutlined, ExclamationCircleOutlined,
    SearchOutlined, FilterOutlined, SortAscendingOutlined,
    ClockCircleOutlined, BookOutlined, StarOutlined,
    GiftOutlined, ClearOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import publicCourseService from "../../services/publicCourseService";
import courseService from "../../services/course.service";
import authHeader from "../../services/auth-header";
import API_BASE from "../../config/apiConfig";
import PaymentWrapper from "../payment/PaymentWrapper";

const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE_URL = `${API_BASE}/images/proxy`;

/* ============ Couleurs logo Nomidya ============ */
const C = {
    blue: "#0054A6",
    blueDark: "#003B75",
    blueLight: "#e8f1fb",
    orange: "#E8860C",
    orangeLight: "#FF8C00",
    orangeBg: "#fff8f0",
    text: "#333",
    textLight: "#888",
    border: "#eaedf2",
    bg: "#f7f8fa",
};

const CoursesByCategory = () => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [coursesByCategory, setCoursesByCategory] = useState({});

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [priceRange, setPriceRange] = useState([0, 1000]);
    const [sortBy, setSortBy] = useState("title");
    const [showFilters, setShowFilters] = useState(false);

    const [payingCourseId, setPayingCourseId] = useState(null);
    const [lastPayment, setLastPayment] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [courseToPayFor, setCourseToPayFor] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewCourse, setPreviewCourse] = useState(null);

    // const openPreview = (course) => { setPreviewCourse(course); setPreviewVisible(true); };
    const openPreview = (course) => { navigate(`/course/${course.id}`); };

    const closePreview = () => { setPreviewVisible(false); setPreviewCourse(null); };

    const imageCache = {};
    const getUserId = () => { const user = JSON.parse(localStorage.getItem('user') || '{}'); return user.id || null; };

    const fetchImage = async (imageUrl) => {
        try {
            const response = await fetch(`${API_BASE_URL}?url=${encodeURIComponent(imageUrl)}`, { headers: authHeader() });
            if (!response.ok) throw new Error("Erreur image");
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        } catch (error) { return "/default-image.png"; }
    };

    const getCategoryColor = (category) => {
        const colors = { 'Technologie': 'blue', 'Technology': 'blue', 'Data': 'green', 'IA': 'purple', 'AI': 'purple', 'Business': 'orange', 'Design': 'red', 'Marketing': 'gold', 'Sciences': 'cyan', 'Informatique': 'geekblue' };
        return colors[category] || 'blue';
    };

    const getCourseStatus = (course) => {
        const now = new Date();
        const courseDate = new Date(course.created_at || now);
        const daysDiff = (now - courseDate) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 7) return { type: 'new', label: 'Nouveau', color: '#52c41a' };
        if (course.rating >= 4.5) return { type: 'popular', label: 'Populaire', color: C.orange };
        if ((course.latest_price || course.price) === 0) return { type: 'free', label: 'Gratuit', color: C.blue };
        return null;
    };

    const filteredAndSortedCourses = useMemo(() => {
        let filtered = courses.filter(course => {
            const searchMatch = !searchQuery ||
                course.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                course.short_desc?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                course.category?.libelle?.toLowerCase().includes(searchQuery.toLowerCase());
            const categoryMatch = selectedCategory === "all" || course.category?.libelle === selectedCategory;
            const price = course.latest_price || course.price || 0;
            const priceMatch = price >= priceRange[0] && price <= priceRange[1];
            return searchMatch && categoryMatch && priceMatch;
        });
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'price_asc': return (a.latest_price || a.price || 0) - (b.latest_price || b.price || 0);
                case 'price_desc': return (b.latest_price || b.price || 0) - (a.latest_price || a.price || 0);
                case 'rating': return (b.rating || 0) - (a.rating || 0);
                default: return (a.title || '').localeCompare(b.title || '');
            }
        });
        return filtered;
    }, [courses, searchQuery, selectedCategory, priceRange, sortBy]);

    const handleBuyCourse = (course) => {
        // 🔐 Vérifier si l'utilisateur est connecté
        const userId = getUserId();

        if (!userId) {
            // Sauvegarder le cours choisi pour reprise après login
            sessionStorage.setItem('pendingCourse', JSON.stringify({ courseId: course.id }));
            message.info("🔒 Connectez-vous pour acheter ce cours");
            // Petite pause pour que l'utilisateur voie le message
            setTimeout(() => {
                navigate('/login');
            }, 800);
            return;
        }

        // ✅ Utilisateur connecté → procéder normalement
        setSelectedCourse(course);
        setConfirmModalVisible(true);
    };
    const handleConfirmPurchase = () => {
        if (!selectedCourse) return;
        const userId = getUserId();

        if (!userId) {
            // Sécurité : si jamais l'utilisateur arrive ici sans être connecté
            setConfirmModalVisible(false);
            setSelectedCourse(null);
            sessionStorage.setItem('pendingCourse', JSON.stringify({ courseId: selectedCourse.id }));
            message.info("🔒 Connectez-vous pour finaliser l'achat");
            setTimeout(() => navigate('/login'), 800);
            return;
        }

        setConfirmModalVisible(false);
        setSelectedCourse(null);
        setCourseToPayFor(selectedCourse);
        setPaymentModalVisible(true);
    };

    const handlePaymentSuccess = (paymentResult) => {
        const { paymentIntent, course } = paymentResult;
        setLastPayment({ id: paymentIntent.id, courseTitle: course.title, amount: (paymentIntent.amount / 100).toFixed(2), currency: paymentIntent.currency.toUpperCase(), status: paymentIntent.status });
        setPaymentModalVisible(false);
        setCourseToPayFor(null);
        message.success(`Félicitations ! Vous avez acheté "${course.title}" avec succès !`);
        setTimeout(() => { navigate('/my-purchases'); }, 3000);
    };

    const handlePaymentError = (error) => { console.error('Erreur de paiement:', error); };
    const handlePaymentCancel = () => { setPaymentModalVisible(false); setCourseToPayFor(null); };

    const groupCoursesByCategory = (coursesData, categoriesData) => {
        const grouped = {};
        categoriesData.forEach(category => { grouped[category.libelle] = []; });
        coursesData.forEach(course => {
            const categoryName = course.category?.libelle || course.category || 'Autres';
            if (!grouped[categoryName]) grouped[categoryName] = [];
            grouped[categoryName].push(course);
        });
        Object.keys(grouped).forEach(key => { if (grouped[key].length === 0) delete grouped[key]; });
        return grouped;
    };

    const clearFilters = () => { setSearchQuery(""); setSelectedCategory("all"); setPriceRange([0, 1000]); setSortBy("title"); };

    const sortMenu = (
        <Menu onClick={({ key }) => setSortBy(key)}>
            <Menu.Item key="title">Titre (A-Z)</Menu.Item>
            <Menu.Item key="price_asc">Prix croissant</Menu.Item>
            <Menu.Item key="price_desc">Prix décroissant</Menu.Item>
            <Menu.Item key="rating">Mieux notés</Menu.Item>
        </Menu>
    );

    /* ============ Carte de cours — COMPACT ============ */
    const CourseCard = ({ course }) => {
        const status = getCourseStatus(course);
        const price = course.latest_price || course.price || 0;

        return (
            <Col xs={24} sm={12} md={8} lg={6} key={course.id}>
                <Badge.Ribbon text={status?.label} color={status?.color} style={{ display: status ? 'block' : 'none' }}>
                    <div style={{
                        borderRadius: 12, overflow: "hidden", background: "white",
                        border: `1px solid ${C.border}`, transition: "all 0.3s ease",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)", cursor: "pointer",
                        display: "flex", flexDirection: "column", height: "100%",
                    }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 8px 24px ${C.blue}12`; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = C.blue; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = C.border; }}
                    >
                        {/* Image */}
                        <div style={{ height: 160, overflow: "hidden", position: "relative", background: C.bg }}>
                            <img alt={course.title} src={course.imageUrl || "/default-image.png"}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>

                        {/* Body */}
                        <div style={{ padding: "14px 14px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
                            <h3 style={{
                                color: C.blueDark, margin: "0 0 6px", fontSize: 14, fontWeight: 700,
                                lineHeight: 1.4, height: 40, overflow: "hidden",
                                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                            }}>
                                {course.title}
                            </h3>

                            <p style={{
                                margin: "0 0 10px", fontSize: 12, color: C.textLight, lineHeight: 1.5,
                                height: 36, overflow: "hidden",
                                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                            }}>
                                {course.description || course.short_desc || "Découvrez ce cours passionnant"}
                            </p>

                            {/* Rating */}
                            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                                <Rate disabled defaultValue={course.rating || 4} style={{ fontSize: 12 }} />
                                <span style={{ marginLeft: 6, fontSize: 11, color: C.textLight }}>({course.rating || 4.0})</span>
                            </div>

                            {/* Info bar */}
                            <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "8px 0", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
                                marginBottom: 10, fontSize: 11, color: "#555",
                            }}>
                                <span><BookOutlined style={{ color: C.blue, marginRight: 4 }} />{course.lessons || "N/A"} leçons</span>
                                <span><ClockCircleOutlined style={{ color: "#00875A", marginRight: 4 }} />{course.duration || "N/A"}</span>
                            </div>

                            {/* Prix */}
                            <div style={{ textAlign: "center", marginBottom: 12 }}>
                                {price > 0 ? (
                                    <div>
                                        <span style={{ fontSize: 20, fontWeight: 800, color: C.blue }}>{price} USD</span>
                                        {course.before_price && course.before_price > price && (
                                            <div style={{ fontSize: 12, color: "#bbb", textDecoration: "line-through" }}>{course.before_price} USD</div>
                                        )}
                                    </div>
                                ) : (
                                    <span style={{ fontSize: 18, fontWeight: 800, color: "#00875A" }}><GiftOutlined /> GRATUIT</span>
                                )}
                            </div>

                            {/* Boutons */}
                            <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                                <Button size="small" style={{
                                    flex: 1, borderColor: C.blue, color: C.blue, fontWeight: 600,
                                    height: 34, borderRadius: 8, fontSize: 12,
                                }} icon={<EyeOutlined />} onClick={() => openPreview(course)}>
                                    Aperçu
                                </Button>
                                <Button type="primary" size="small" style={{
                                    flex: 2, background: C.blue, border: "none", fontWeight: 700,
                                    height: 34, borderRadius: 8, fontSize: 12,
                                    boxShadow: `0 2px 8px ${C.blue}22`,
                                }} icon={<ShoppingCartOutlined />}
                                    loading={payingCourseId === course.id}
                                    onClick={() => handleBuyCourse(course)}>
                                    {price > 0 ? "Acheter" : "S'inscrire"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Badge.Ribbon>
            </Col>
        );
    };

    useEffect(() => {
        const fetchCoursesAndCategories = async () => {
            try {
                setLoading(true);
                const [categoriesResponse, coursesResponse] = await Promise.all([
                    publicCourseService.getCategories(),
                    publicCourseService.getCourseSummary()
                ]);
                setCategories(categoriesResponse || []);
                if (!coursesResponse || coursesResponse.length === 0) { setCourses([]); setCoursesByCategory({}); return; }
                const prices = coursesResponse.map(c => c.latest_price || c.price || 0);
                const maxPrice = Math.max(...prices, 100);
                setPriceRange([0, maxPrice]);
                const updatedCourses = coursesResponse.slice(0, 50).map((course) => {
                    if (course.image) {
                        const imageUrl = `${API_BASE_URL}?url=${encodeURIComponent(course.image)}`;
                        return { ...course, imageUrl };
                    }
                    return { ...course, imageUrl: "/default-image.png" };
                });
                setCourses(updatedCourses);
                const grouped = groupCoursesByCategory(updatedCourses, categoriesResponse || []);
                setCoursesByCategory(grouped);
            } catch (err) { setError("Impossible de récupérer les cours."); console.error(err); }
            finally { setLoading(false); }
        };
        fetchCoursesAndCategories();
    }, []);

    if (loading) {
        return (
            <div style={{
                minHeight: '60vh',
                background: '#f7f8fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
            }}>
                <style>{`
                @keyframes nomidya-pulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.7; }
                }

                @keyframes nomidya-rotate {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes nomidya-fade-in {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes nomidya-bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-12px); }
                }

                .nomidya-loader-card {
                    background: white;
                    border-radius: 24px;
                    padding: 60px 50px;
                    box-shadow: 0 25px 50px rgba(0, 84, 166, 0.12);
                    text-align: center;
                    max-width: 500px;
                    width: 100%;
                    animation: nomidya-fade-in 0.6s ease-out;
                    border: 1px solid rgba(0, 84, 166, 0.08);
                }

                .nomidya-loader-icon-wrapper {
                    position: relative;
                    width: 100px;
                    height: 100px;
                    margin: 0 auto 32px;
                }

                .nomidya-loader-ring {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100px;
                    height: 100px;
                    border-radius: 50%;
                    border: 4px solid transparent;
                    border-top-color: ${C.blue};
                    border-right-color: ${C.orange};
                    animation: nomidya-rotate 1.2s linear infinite;
                }

                .nomidya-loader-ring-2 {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    border: 3px solid transparent;
                    border-bottom-color: ${C.blueDark};
                    border-left-color: ${C.orangeLight};
                    animation: nomidya-rotate 1.5s linear infinite reverse;
                }

                .nomidya-loader-center {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 50px;
                    height: 50px;
                    background: linear-gradient(135deg, ${C.blue} 0%, ${C.orange} 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 24px;
                    animation: nomidya-pulse 2s ease-in-out infinite;
                    box-shadow: 0 8px 24px rgba(0, 84, 166, 0.3);
                }

                .nomidya-loader-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: ${C.blueDark};
                    margin: 0 0 8px;
                    letter-spacing: -0.3px;
                }

                .nomidya-loader-subtitle {
                    font-size: 14px;
                    color: ${C.textLight};
                    margin: 0 0 24px;
                    line-height: 1.6;
                }

                .nomidya-loader-dots {
                    display: flex;
                    justify-content: center;
                    gap: 10px;
                    margin-top: 20px;
                }

                .nomidya-loader-dot {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    animation: nomidya-bounce 1.4s ease-in-out infinite;
                }

                .nomidya-loader-dot:nth-child(1) {
                    background: ${C.blue};
                    animation-delay: 0s;
                }

                .nomidya-loader-dot:nth-child(2) {
                    background: ${C.orange};
                    animation-delay: 0.2s;
                }

                .nomidya-loader-dot:nth-child(3) {
                    background: ${C.blueDark};
                    animation-delay: 0.4s;
                }

                .nomidya-loader-progress {
                    width: 100%;
                    height: 4px;
                    background: #e8eef5;
                    border-radius: 4px;
                    overflow: hidden;
                    margin-top: 24px;
                }

                .nomidya-loader-progress-bar {
                    height: 100%;
                    background: linear-gradient(90deg, ${C.blue} 0%, ${C.orange} 100%);
                    border-radius: 4px;
                    width: 30%;
                    animation: nomidya-progress 2s ease-in-out infinite;
                }

                @keyframes nomidya-progress {
                    0% { width: 0%; margin-left: 0%; }
                    50% { width: 70%; margin-left: 15%; }
                    100% { width: 0%; margin-left: 100%; }
                }
            `}</style>

                <div className="nomidya-loader-card">
                    {/* Icône animée centrale */}
                    <div className="nomidya-loader-icon-wrapper">
                        <div className="nomidya-loader-ring"></div>
                        <div className="nomidya-loader-ring-2"></div>
                        <div className="nomidya-loader-center">
                            <BookOutlined />
                        </div>
                    </div>

                    {/* Titre */}
                    <h2 className="nomidya-loader-title">
                        Chargement des formations
                    </h2>

                    {/* Sous-titre */}
                    <p className="nomidya-loader-subtitle">
                        Préparation de votre catalogue personnalisé...
                    </p>

                    {/* Points animés */}
                    <div className="nomidya-loader-dots">
                        <div className="nomidya-loader-dot"></div>
                        <div className="nomidya-loader-dot"></div>
                        <div className="nomidya-loader-dot"></div>
                    </div>

                    {/* Barre de progression */}
                    <div className="nomidya-loader-progress">
                        <div className="nomidya-loader-progress-bar"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return <div style={{ padding: 20 }}><Alert message="Erreur" description={error} type="error" showIcon /></div>;
    }

    return (
        <div style={{ background: "white", padding: 0, fontFamily: "'Outfit', -apple-system, sans-serif" }}>

            {/* ============ Header — Fond blanc, même style ============ */}
            <div style={{ padding: "0 0 28px", borderBottom: `1px solid ${C.border}`, marginBottom: 28 }}>
                <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                        <span style={{
                            fontSize: 11, fontWeight: 700, color: C.orange, letterSpacing: 1.5,
                            textTransform: "uppercase", display: "inline-block",
                            padding: "3px 12px", borderRadius: 12, background: C.orangeBg, marginBottom: 8,
                        }}>
                            Nos formations
                        </span>
                        <h2 style={{
                            color: C.blueDark, fontSize: 28, fontWeight: 800, margin: "8px 0 6px",
                        }}>
                            Apprenez avec les <span style={{ color: C.orange }}>meilleurs instructeurs</span>
                        </h2>
                        <p style={{ fontSize: 14, color: C.textLight, margin: 0 }}>
                            Une plateforme e-learning innovante pour explorer, apprendre et réussir.
                        </p>
                    </div>

                    {/* Barre de recherche */}
                    <div style={{ maxWidth: 600, margin: "0 auto 16px" }}>
                        <Input
                            placeholder="Rechercher un cours, une catégorie..."
                            prefix={<SearchOutlined style={{ color: C.blue }} />}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                borderRadius: 10, fontSize: 13, padding: "8px 14px",
                                border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                            }}
                            suffix={searchQuery && (
                                <Button type="text" size="small" icon={<ClearOutlined />} onClick={() => setSearchQuery("")} />
                            )}
                        />
                    </div>

                    {/* Filtres */}
                    <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 10 }}>
                        <Select value={selectedCategory} onChange={setSelectedCategory}
                            style={{ minWidth: 170 }} dropdownMatchSelectWidth={false}>
                            <Option value="all">Toutes catégories</Option>
                            {categories.map(cat => (
                                <Option key={cat.id} value={cat.libelle}>{cat.libelle}</Option>
                            ))}
                        </Select>

                        <Dropdown overlay={sortMenu} trigger={['click']}>
                            <Button icon={<SortAscendingOutlined />} style={{ borderRadius: 8 }}>Trier</Button>
                        </Dropdown>

                        <Button type={showFilters ? "primary" : "default"} icon={<FilterOutlined />}
                            onClick={() => setShowFilters(!showFilters)} style={{
                                borderRadius: 8,
                                ...(showFilters ? { background: C.blue, borderColor: C.blue } : {}),
                            }}>
                            Filtres
                        </Button>

                        {(searchQuery || selectedCategory !== "all") && (
                            <Button icon={<ClearOutlined />} onClick={clearFilters} style={{ borderRadius: 8 }}>Effacer</Button>
                        )}
                    </div>

                    {/* Filtres avancés */}
                    {showFilters && (
                        <div style={{
                            marginTop: 16, padding: 16, background: C.bg,
                            borderRadius: 10, border: `1px solid ${C.border}`, maxWidth: 400, margin: "16px auto 0",
                        }}>
                            <Text style={{ fontWeight: 600, marginBottom: 8, display: "block", fontSize: 13, color: C.text }}>
                                Plage de prix (USD) :
                            </Text>
                            <Slider range value={priceRange} onChange={setPriceRange} max={1000} step={10}
                                marks={{ 0: '0', 500: '500', 1000: '1000+' }} />
                        </div>
                    )}
                </div>
            </div>

            {/* ============ Grille de cours ============ */}
            <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 8px" }}>
                {lastPayment && (
                    <Alert style={{ marginBottom: 24, borderRadius: 10 }}
                        type={lastPayment.status === "succeeded" ? "success" : "info"} showIcon
                        message={`Paiement réussi pour "${lastPayment.courseTitle}"`}
                        description={<div>
                            <div><strong>Montant:</strong> {lastPayment.amount} {lastPayment.currency}</div>
                            <div><strong>Statut:</strong> {lastPayment.status}</div>
                        </div>}
                        closable onClose={() => setLastPayment(null)} />
                )}

                {(searchQuery || selectedCategory !== "all") && (
                    <div style={{ marginBottom: 16, textAlign: "center" }}>
                        <Text style={{ fontSize: 13, color: C.textLight }}>
                            {filteredAndSortedCourses.length} cours trouvé(s)
                            {searchQuery && ` pour "${searchQuery}"`}
                            {selectedCategory !== "all" && ` dans "${selectedCategory}"`}
                        </Text>
                    </div>
                )}

                <Row gutter={[16, 16]}>
                    {filteredAndSortedCourses.map((course) => (
                        <CourseCard key={course.id} course={course} />
                    ))}
                </Row>

                {filteredAndSortedCourses.length === 0 && (
                    <div style={{ textAlign: "center", margin: "60px 0" }}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<span style={{ fontSize: 14, color: C.textLight }}>
                                {searchQuery ? `Aucun cours trouvé pour "${searchQuery}"` : "Aucun cours disponible"}
                            </span>}>
                            {searchQuery && (
                                <Button type="primary" onClick={clearFilters}
                                    style={{ borderRadius: 8, background: C.blue, borderColor: C.blue }}>
                                    Voir tous les cours
                                </Button>
                            )}
                        </Empty>
                    </div>
                )}
            </div>

            {/* ============ MODALS ============ */}
            <Modal
                title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ExclamationCircleOutlined style={{ color: C.orange }} />
                    <span>Confirmer votre achat</span>
                </div>}
                open={confirmModalVisible}
                onOk={handleConfirmPurchase}
                onCancel={() => { setConfirmModalVisible(false); setSelectedCourse(null); }}
                okText="Procéder au paiement" cancelText="Annuler"
                okButtonProps={{ style: { backgroundColor: C.blue, borderColor: C.blue, borderRadius: 8 } }}
                width={480}>
                {selectedCourse && (
                    <div style={{ padding: '12px 0' }}>
                        <div style={{
                            display: 'flex', gap: 14, marginBottom: 16, padding: 14,
                            background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`,
                        }}>
                            <img src={selectedCourse.imageUrl || "/default-image.png"} alt={selectedCourse.title}
                                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 10 }} />
                            <div style={{ flex: 1 }}>
                                <h3 style={{ margin: '0 0 4px', color: C.blueDark, fontSize: 15, fontWeight: 700 }}>
                                    {selectedCourse.title}
                                </h3>
                                <p style={{ margin: '0 0 4px', color: C.textLight, fontSize: 12 }}>
                                    {selectedCourse.lessons || 'N/A'} leçons
                                </p>
                                <div style={{ fontSize: 18, fontWeight: 800, color: C.blue }}>
                                    {(selectedCourse.latest_price || selectedCourse.price) ?
                                        `${selectedCourse.latest_price || selectedCourse.price} USD` : "GRATUIT"}
                                </div>
                            </div>
                        </div>
                        <p style={{ textAlign: 'center', fontSize: 13, color: C.textLight }}>
                            Cliquez sur "Procéder au paiement" pour continuer.
                        </p>
                    </div>
                )}
            </Modal>

            {/* Preview modal */}
            <Modal open={previewVisible} onCancel={closePreview} footer={null}
                bodyStyle={{ padding: 0 }} width={900} style={{ maxWidth: "95vw" }} title={null}>
                {previewCourse && (
                    <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 0 }}>
                        <div style={{ position: "relative", height: 280, overflow: "hidden" }}>
                            <img src={previewCourse.imageUrl || "/default-image.png"} alt={previewCourse.title}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            {getCourseStatus(previewCourse) && (
                                <div style={{
                                    position: "absolute", top: 10, left: 10,
                                    background: getCourseStatus(previewCourse).color, color: "#fff",
                                    padding: "3px 10px", borderRadius: 12, fontWeight: 700, fontSize: 11,
                                }}>{getCourseStatus(previewCourse).label}</div>
                            )}
                        </div>
                        <div style={{ padding: 20 }}>
                            <h2 style={{ margin: "0 0 6px", fontSize: 20, color: C.blueDark }}>{previewCourse.title}</h2>
                            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", margin: "6px 0 12px", color: "#555", fontSize: 12 }}>
                                <span><BookOutlined /> {previewCourse.lessons ?? "N/A"} leçons</span>
                                <span><ClockCircleOutlined /> {previewCourse.duration ?? "N/A"}</span>
                                <span><StarOutlined /> {previewCourse.rating ?? 4.0}</span>
                                {previewCourse.category?.libelle && (
                                    <Tag color={getCategoryColor(previewCourse.category.libelle)} style={{ fontSize: 11 }}>
                                        {previewCourse.category.libelle}
                                    </Tag>
                                )}
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                {(previewCourse.latest_price ?? previewCourse.price ?? 0) > 0 ? (
                                    <span style={{ fontSize: 22, fontWeight: 800, color: C.blue }}>
                                        {(previewCourse.latest_price ?? previewCourse.price)} USD
                                    </span>
                                ) : (
                                    <span style={{ fontSize: 20, fontWeight: 800, color: "#00875A" }}><GiftOutlined /> GRATUIT</span>
                                )}
                            </div>
                            <div style={{ maxHeight: 120, overflow: "auto", fontSize: 13, color: "#555", lineHeight: 1.6 }}>
                                <p>{previewCourse.short_desc || "Aucune description disponible."}</p>
                                {previewCourse.overview && (<><h4 style={{ marginTop: 10, color: C.blueDark }}>Aperçu</h4><p style={{ whiteSpace: "pre-line" }}>{previewCourse.overview}</p></>)}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                                <Button onClick={closePreview} style={{ borderRadius: 8 }}>Fermer</Button>
                                <Button type="primary" icon={<ShoppingCartOutlined />}
                                    style={{ borderRadius: 8, background: C.blue, borderColor: C.blue }}
                                    onClick={() => { closePreview(); handleBuyCourse(previewCourse); }}>
                                    {(previewCourse.latest_price ?? previewCourse.price ?? 0) > 0 ? "Acheter" : "S'inscrire"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <PaymentWrapper visible={paymentModalVisible} course={courseToPayFor}
                onSuccess={handlePaymentSuccess} onError={handlePaymentError} onCancel={handlePaymentCancel} />
        </div>
    );
};

export default CoursesByCategory;